import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { postData } from './api';

interface FeedbackState {
  submittingFeedback: boolean;
  feedbackError: string | string[] | null;
  watchedVideos: number[];
  showFeedback: boolean;
}

const initialState: FeedbackState = {
  submittingFeedback: false,
  feedbackError: null,
  watchedVideos: [],
  showFeedback: false,
};

export const submitFeedback = createAsyncThunk<
  unknown,
  { record_id: number; feedback: string; title: string },
  { state: RootState }
>(
  'feedback/submitFeedback',
  async ({ record_id, feedback, title }, { rejectWithValue }) => {
    try {
      return await postData({
        endpoint: 'enrichment_center_feedback/',
        payload: { record_id, feedback, title },
      });
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  {
    condition: (_, { getState }) => !getState().feedback.submittingFeedback,
  }
);

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    addWatchedVideo: (state, action: PayloadAction<number>) => {
      const newSet = new Set([...state.watchedVideos, action.payload]);
      state.watchedVideos = [...newSet];
    },
    toggleFeedback: (state, action: PayloadAction<boolean>) => {
      state.showFeedback = action.payload;
    },
    resetWatchedVideos: (state) => {
      state.watchedVideos = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitFeedback.pending, (state) => {
        state.submittingFeedback = true;
        state.feedbackError = null;
      })
      .addCase(submitFeedback.fulfilled, (state) => {
        state.submittingFeedback = false;
        state.feedbackError = null;
      })
      .addCase(submitFeedback.rejected, (state, action) => {
        state.submittingFeedback = false;
        state.feedbackError = (action.payload as string | string[]) ?? action.error.message ?? null;
      });
  },
});

export const { addWatchedVideo, toggleFeedback, resetWatchedVideos } = feedbackSlice.actions;
export default feedbackSlice.reducer;
